<template>
  <div
    class="potential-bidder-tag"
    :class="({ overview: !potentialBidder, disabled }, `__variant-${variant} __size-${size}`)"
    @click="onClick"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
  >
    <div v-if="size === 'compact'">
      <div class="__content-container">
        <div class="__row">
          <span
            ref="badgeContainer"
            :key="potentialBidder.id"
            class="__title"
            v-tooltip="tooltipOptions(badgeContainer, potentialBidder.organization_name)"
          >
            {{ potentialBidder.organization_name }}
          </span>
          <BaseIcon
            v-if="submitted"
            name="assignment-turned-in"
            class="potential-bidder-tag__submitted-icon"
            v-tooltip="$t('tender-details.potential-bidders.submitted-tooltip')"
          />
          <BaseIcon
            name="verified-badge"
            v-if="tenantHasBusinessIntelligenceAccess() && !potentialBidder.is_manual_entry"
            v-tooltip="source ? $t('page.business-intelligence.official-results', { source }) : ''"
          />
          <BIBadgeActionMenu
            class="action-button"
            v-if="
              tenantHasBusinessIntelligenceAccess() && !(potentialBidder.is_tenant || potentialBidder.is_competitor)
            "
            type="potential-bidder"
            :name="potentialBidder.organization_name"
            :hover="hover"
            :hasWinner="hasWinner"
            :isExistingCompetitor="potentialBidder.is_existing_competitor"
            :isTenant="potentialBidder.is_tenant"
            @add-competitor="onAddCompetitorClicked"
            @edit-competitor="onEditCompetitorClicked"
            @add-winner="onAddWinnerClicked"
          />
        </div>
        <div class="__row">
          <span
            ref="emailContainer"
            :key="potentialBidder.id"
            class="__email"
            v-tooltip="tooltipOptions(emailContainer, email)"
          >
            {{ email }}
          </span>
          <BIBadgeCompetitorMenu
            class="action-button"
            v-if="
              tenantHasBusinessIntelligenceAccess() && !(potentialBidder.is_tenant || potentialBidder.is_competitor)
            "
            :name="potentialBidder.organization_name"
            :hover="hover"
            :isExistingCompetitor="potentialBidder.is_existing_competitor"
            @action-succeeded="onActionSucceeded"
          />
        </div>
      </div>
    </div>
    <div v-if="size === 'full'">
      <div class="potential-bidder-tag__header">
        <div class="potential-bidder-tag__organization">
          <span
            ref="badgeContainer"
            :key="potentialBidder.id"
            class="potential-bidder-tag__organization-name"
            v-tooltip="tooltipOptions(badgeContainer, potentialBidder.organization_name)"
          >
            {{ potentialBidder.organization_name }}
          </span>
          <BaseTag
            v-if="hasBidRatio"
            preset="soft"
            tone="neutral"
            class="potential-bidder-tag__bid-ratio"
            v-tooltip="bidRatioTooltip"
          >
            {{ bidRatioPercent }}
          </BaseTag>
          <BaseTag
            v-else-if="showBidRatioNotAvailable"
            preset="soft"
            tone="neutral"
            class="potential-bidder-tag__bid-ratio"
            v-tooltip="$t('tender-details.potential-bidders.bid-ratio-tooltip.not-competitor')"
          >
            {{ $t('tender-details.potential-bidders.bid-ratio-not-available') }}
          </BaseTag>
        </div>
        <!-- Phones: its actions in a sheet, as a row's ⋯ (docs/page-layout.md). -->
        <BaseMoreActions
          v-if="isPhone && actionable && potentialBidder.is_manual_entry && !disabled"
          :title="potentialBidder.organization_name"
          :actions="bidderActions"
          @select="onBidderAction"
        />
        <v-menu v-else v-model="actionMenu" absolute :disabled="disabled">
          <template v-slot:activator="{ on, attrs }">
            <div
              v-if="actionable && potentialBidder.is_manual_entry"
              class="potential-bidder-tag__action"
              @click.stop
              v-on="on"
              v-bind="attrs"
            >
              <BaseIcon class="potential-bidder-tag__action-icon" name="more-horizontal" />
            </div>
          </template>
          <div class="base-actions-menu">
            <ul class="base-actions-menu__ul">
              <li class="base-actions-menu__li" @click="onEditClick">
                <BaseIcon :size="44" name="edit-2" />
                {{ $t('actions.edit') }}
              </li>
              <li class="base-actions-menu__li delete" @click="onDeleteClick">
                <BaseIcon :size="44" name="trash-2" />
                {{ $t('actions.delete') }}
              </li>
            </ul>
          </div>
        </v-menu>
        <!-- todo: change once we allow entry of potential bidders -->
        <div
          v-if="actionable && tenantHasBusinessIntelligenceAccess() && !potentialBidder.is_manual_entry"
          class="potential-bidder-tag__icons-container"
        >
          <BaseIcon
            v-if="submitted"
            name="assignment-turned-in"
            class="potential-bidder-tag__submitted-icon"
            v-tooltip="$t('tender-details.potential-bidders.submitted-tooltip')"
          />
          <BaseIcon
            name="verified-badge"
            v-tooltip="source ? $t('page.business-intelligence.official-results', { source }) : ''"
          />
          <BIBadgeCompetitorMenu
            class="action-button"
            v-if="!(potentialBidder.is_tenant || potentialBidder.is_competitor)"
            :name="potentialBidder.organization_name"
            :hover="hover"
            :isExistingCompetitor="potentialBidder.is_existing_competitor"
            @action-succeeded="onActionSucceeded()"
          />
          <BIBadgeActionMenu
            class="action-button"
            v-if="!(potentialBidder.is_tenant || potentialBidder.is_competitor)"
            type="potential-bidder"
            :name="potentialBidder.organization_name"
            :hover="hover"
            :hasWinner="hasWinner"
            :isExistingCompetitor="potentialBidder.is_existing_competitor"
            :isTenant="potentialBidder.is_tenant"
            @add-competitor="onAddCompetitorClicked"
            @edit-competitor="onEditCompetitorClicked"
            @add-winner="onAddWinnerClicked"
          />
        </div>
      </div>
      <div class="potential-bidder-tag__content">
        <span class="potential-bidder-tag__organization-info" v-if="potentialBidder.address">
          {{ potentialBidder.address }}
        </span>
        <span class="potential-bidder-tag__organization-info" v-if="potentialBidder.website">
          <BaseLink target-blank :href="potentialBidder.website">
            {{ potentialBidder.website }}
          </BaseLink>
        </span>

        <span
          class="potential-bidder-tag__divider"
          v-if="potentialBidder.contact_points && potentialBidder.contact_points.length > 0"
        >
        </span>

        <div v-for="(contactPoint, index) in potentialBidder.contact_points" :key="index">
          <div class="potential-bidder-tag__contact-info" v-if="contactPoint.name">
            {{ contactPoint.name }}
          </div>
          <div class="potential-bidder-tag__contact-info" v-if="contactPoint.email">
            {{ contactPoint.email }}
          </div>
          <div class="potential-bidder-tag__contact-info" v-if="contactPoint.telephone">
            {{ formatPhoneNumber(contactPoint.telephone) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseLink from '@/components/base/BaseLink';
import BaseTag from '@/components/base/BaseTag.vue';
import BIBadgeActionMenu from '@/components/page-business-intelligence/BIBadgeActionMenu.vue';
import BIBadgeCompetitorMenu from '@/components/page-business-intelligence/BIBadgeCompetitorMenu.vue';

import { tenantHasBusinessIntelligenceAccess } from '@/util/module-access-util';
import { viewport } from '@/util/viewport';
import { formatPhoneNumber } from '@/util/phone-util';
import { formatLocalizedNumber, formatPercentageShort } from '@/util/number-util';

export default {
  name: 'BasePotentialBidder',
  components: {
    BaseIcon,
    BaseLink,
    BaseMoreActions,
    BaseTag,
    BIBadgeActionMenu,
    BIBadgeCompetitorMenu,
  },
  props: {
    potentialBidder: {
      type: Object,
      required: true,
    },
    actionable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: 'full',
    },
    source: {
      type: String,
      required: false,
      default: null,
    },
    hasWinner: {
      type: Boolean,
      required: false,
      default: false,
    },
    submitted: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return { badgeContainer: null, emailContainer: null, actionMenu: false, hover: false };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    bidderActions() {
      return [
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    variant() {
      if (this.potentialBidder.is_competitor) {
        return 'active';
      } else if (this.potentialBidder.is_tenant) {
        return 'emphasis';
      } else {
        return 'default';
      }
    },
    hasBidRatio() {
      const ratio = this.potentialBidder && this.potentialBidder.bid_ratio;
      return typeof ratio === 'number' && !isNaN(ratio);
    },
    isCompetitor() {
      return !!(this.potentialBidder.is_competitor || this.potentialBidder.is_existing_competitor);
    },
    showBidRatioNotAvailable() {
      return tenantHasBusinessIntelligenceAccess() && !this.isCompetitor && !this.potentialBidder.is_tenant;
    },
    bidRatioPercent() {
      return formatPercentageShort(this.potentialBidder.bid_ratio);
    },
    bidRatioTooltip() {
      const title = this.$t('tender-details.potential-bidders.bid-ratio-tooltip.title');
      const details = this.$t('tender-details.potential-bidders.bid-ratio-tooltip.details', {
        converted: formatLocalizedNumber(this.potentialBidder.potential_bids_converted || 0),
        potential: formatLocalizedNumber(this.potentialBidder.potential_bids || 0),
      });
      return `${title}<br>${details}`;
    },
    email() {
      if (
        this.potentialBidder &&
        this.potentialBidder.contact_points &&
        this.potentialBidder.contact_points.length > 0
      ) {
        return this.potentialBidder.contact_points[0].email || '-';
      }
      return '-';
    },
  },
  methods: {
    formatPhoneNumber,
    tenantHasBusinessIntelligenceAccess,
    onBidderAction(key) {
      if (key === 'edit') this.onEditClick();
      else this.onDeleteClick();
    },
    onEditClick() {
      this.$emit('edit-potential-bidder', this.potentialBidder);
    },
    onDeleteClick() {
      this.$emit('delete-potential-bidder', this.potentialBidder);
    },
    onClick() {
      if (!this.disabled) {
        this.$emit('potential-bidder-selected', this.potentialBidder, true);
      }
    },
    tooltipOptions(container, text) {
      if (container && this.isTruncated(container, text)) {
        return {
          content: text,
        };
      }
      return {};
    },
    isTruncated(container, text) {
      if (text && container) {
        // Compare scrollWidth (full content width) with clientWidth (visible width)
        return container.scrollWidth > container.clientWidth;
      } else {
        return false;
      }
    },
    onAddCompetitorClicked(name) {
      this.$emit('add-competitor', name);
    },
    onEditCompetitorClicked(competitor, name) {
      this.$emit('edit-competitor', competitor, name);
    },
    onAddWinnerClicked() {
      this.$emit('add-winner', this.potentialBidder);
    },
    onActionSucceeded() {
      this.$emit('action-succeeded');
    },
  },
  mounted() {
    if (this.$refs.badgeContainer) {
      this.badgeContainer = this.$refs.badgeContainer;
    }
    if (this.$refs.emailContainer) {
      this.emailContainer = this.$refs.emailContainer;
    }
  },
  updated() {
    if (this.$refs.badgeContainer) {
      this.badgeContainer = this.$refs.badgeContainer;
    }
    if (this.$refs.emailContainer) {
      this.emailContainer = this.$refs.emailContainer;
    }
  },
};
</script>

<style lang="scss">
.potential-bidder-tag {
  width: 100%;
  border-radius: 5px;
  color: $color-normal;
  background-color: $color-white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px;
  transition: all 200ms ease;

  &.__size-compact {
    min-height: 60px;
    width: 180px;
    padding: 0px 8px;

    .__content-container {
      display: grid;
      grid-template-rows: auto;
      width: 100%;
    }
  }

  &.tooltip {
    border: 1px $color-border solid;
  }

  &.__variant-default {
    border: 1px solid $color-neutral;
  }

  &.__variant-emphasis {
    border: 1px solid $color-accent;
  }

  &.__variant-active {
    background-color: $color-accent-pale;
    border: 1px solid $color-accent;
  }

  &__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;

    .base-icon {
      margin-right: 4px;
      color: $color-warm-blue;
    }
  }

  &.overview {
    min-width: auto;
  }

  &.disabled {
    .potential-bidder-tag__action-icon {
      color: rgba(0, 0, 0, 0.38);
    }
  }

  &__organization {
    flex: 1 1 auto;
    font-weight: bold;
    font-size: $font-size-small;

    &-name {
      text-overflow: ellipsis;
      overflow: hidden;
      flex: 1 1 auto;
      max-width: 185px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
  }

  &__organization-info {
    font-size: $font-size-small;
    color: $color-normal-pale;
  }

  &__divider {
    background: $color-border;
    display: block;
    height: 1px;
    width: 100%;
    margin-bottom: 6px;
    margin-top: 4px;
  }

  &__contact-info {
    font-size: $font-size-small;
    color: $color-normal-pale;
  }

  &__action {
    display: flex;
    align-items: center;
    margin-left: 12px;
    cursor: pointer;

    &-icon {
      height: 16px;
      width: 16px;
      color: $color-normal-pale;
      transition: color 200ms ease;

      &:hover {
        color: $color-normal;
        transition: color 200ms ease;
      }
    }
  }

  &__submitted-icon {
    color: $color-green-600 !important;
    flex-shrink: 0;
  }

  &__bid-ratio {
    flex-shrink: 0;
    margin-right: 8px;
  }

  &__icons-container {
    display: flex;
    align-items: center;
    gap: 4px;

    .base-icon {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
    }
  }

  .__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    overflow: hidden;
    min-height: 30px;

    &:first-child {
      padding: 8px 8px 0px 8px;
    }

    &:last-child {
      padding: 4px 8px 8px 8px;
    }

    .base-icon {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
    }

    .__title,
    .__email {
      flex-grow: 1; /* the only item that grows/shrinks */
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: $font-size-xsmall;
    }

    .__title {
      font-weight: 700;
    }

    .__email {
      color: $color-normal-pale;
    }
  }
}
</style>
